import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule } from '@angular/material/dialog';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { NgOtpInputModule } from 'ng-otp-input';
import { TranslateModule } from '@ngx-translate/core';
import { LottieModule } from 'ngx-lottie';
import { CardInscriptionSelectionComponent } from './card-inscription-selection/card-inscription-selection.component';
import { FooterComponent } from './footer/footer.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { ConfirmOpenComponent } from './confirm-open/confirm-open.component';
import { VerificationChargeInfoComponent } from './verification-charge-info/verification-charge-info.component';
import { HeaderComponent } from './header/header.component';
import { CurrencyClpPipe } from './pipes/currency-clp.pipe';
import { ActionButtonFooterComponent } from './action-button-footer/action-button-footer.component';
import { OtpInputComponent } from './otp-input/otp-input.component';
import { PhoneInputComponent } from './phone-input/phone-input.component';
import { EditableInputComponent } from './editable-input/editable-input.component';
import { IncognitoModalComponent } from './incognito-modal/incognito-modal.component';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { ParsePricePipe } from './pipes/parse-price.pipe';
import { ConfirmDocumentInscriptionDialogComponent } from './action-button-footer/confirm-document-inscription-dialog/confirm-document-inscription-dialog.component';
import { PaymentMethodErrorDialogComponent } from './action-button-footer/payment-method-error-dialog/payment-method-error-dialog.component';
import { StarRatingComponent } from './star-rating/star-rating.component';
import { ToolbarComponent } from './toolbar/toolbar.component';
import { SecondaryToolbarComponent } from './toolbar/secondary-toolbar/secondary-toolbar.component';
import { AmipassRechargeModalComponent } from './amipass-recharge-modal/amipass-recharge-modal.component';
import { FancyCardComponent } from './fancy-card/fancy-card.component';
import { FancyCardBackComponent } from './fancy-card-back/fancy-card-back.component';
import { BalanceRechargeSelectionComponent } from './balance-recharge-selection/balance-recharge-selection.component';
import { EdenredRechargeModalComponent } from './edenred-recharge-modal/edenred-recharge-modal.component';
import { AuthNavbarComponent } from './auth-navbar/auth-navbar.component';
import { FieldEditFormComponent } from './field-edit-form/field-edit-form.component';
import { CloseChipComponent } from './close-chip/close-chip.component';
import { UnderlineButtonComponent } from './underline-button/underline-button.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { FloatingBannerComponent } from './floating-banner/floating-banner.component';
import { BackHeaderComponent } from './back-header/back-header.component';
import { IconBadgeHeaderComponent } from './icon-badge-header/icon-badge-header.component';
import { IssueStatusChipComponent } from './issue-status-chip/issue-status-chip.component';
import { CookiePreferencesComponent } from './cookie-preferences/cookie-preferences.component';

@NgModule({
  declarations: [
    CardInscriptionSelectionComponent,
    AmipassRechargeModalComponent,
    EdenredRechargeModalComponent,
    ToolbarComponent,
    SecondaryToolbarComponent,
    FooterComponent,
    SidebarComponent,
    ConfirmOpenComponent,
    VerificationChargeInfoComponent,
    HeaderComponent,
    CurrencyClpPipe,
    ActionButtonFooterComponent,
    OtpInputComponent,
    PhoneInputComponent,
    EditableInputComponent,
    IncognitoModalComponent,
    SafeUrlPipe,
    ParsePricePipe,
    ConfirmDocumentInscriptionDialogComponent,
    PaymentMethodErrorDialogComponent,
    StarRatingComponent,
    FancyCardComponent,
    FancyCardBackComponent,
    BalanceRechargeSelectionComponent,
    AuthNavbarComponent,
    FieldEditFormComponent,
    ErrorDialogComponent,
    FloatingBannerComponent,
    IssueStatusChipComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    LottieModule,
    RouterModule,
    NgOtpInputModule,
    MatRippleModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatToolbarModule,
    MatDialogModule,
    MatBottomSheetModule,
    MatSidenavModule,
    MatListModule,
    MatInputModule,
    MatCardModule,
    MatFormFieldModule,
    MatStepperModule,
    MatSelectModule,
    MatSnackBarModule,
    MatExpansionModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    ReactiveFormsModule,
    CloseChipComponent,
    UnderlineButtonComponent,
    // Standalone; opened as a dialog from the cookie banner's (CookieBannerComponent) and the
    // help section (FaqComponent).
    CookiePreferencesComponent,
    // Standalone header pair used by the full-screen dialogs; re-exported for the legacy
    // NgModule consumers that still resolve them through this module.
    BackHeaderComponent,
    IconBadgeHeaderComponent,
  ],
  exports: [
    CommonModule,
    FormsModule,
    RouterModule,
    HttpClientModule,
    MatCheckboxModule,
    MatRippleModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatInputModule,
    MatCardModule,
    MatFormFieldModule,
    MatDialogModule,
    MatBottomSheetModule,
    MatStepperModule,
    MatSelectModule,
    MatSnackBarModule,
    MatExpansionModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    ReactiveFormsModule,
    // `AngularFireAnalyticsModule` is deliberately not re-exported: re-exporting it
    // makes every importer of SharedModule import it too, and its constructor eagerly loads the
    // analytics SDK — which injects gtag.js before the user has answered the cookie banner.
    // `AngularFireAnalytics` stays injectable; see the note in AppModule.
    AngularFireAuthModule,
    AngularFirestoreModule,
    NgxSpinnerModule,
    ZXingScannerModule, // Move this to specific module/component
    NgOtpInputModule,
    TranslateModule,
    LottieModule,
    ToolbarComponent,
    SecondaryToolbarComponent,
    FooterComponent,
    SidebarComponent,
    ActionButtonFooterComponent,
    OtpInputComponent,
    PhoneInputComponent,
    CurrencyClpPipe,
    EditableInputComponent,
    StarRatingComponent,
    AmipassRechargeModalComponent,
    EdenredRechargeModalComponent,
    BalanceRechargeSelectionComponent,
    FancyCardComponent,
    FancyCardBackComponent,
    AuthNavbarComponent,
    FieldEditFormComponent,
    ErrorDialogComponent,
    FloatingBannerComponent,
    CloseChipComponent,
    UnderlineButtonComponent,
    BackHeaderComponent,
    IconBadgeHeaderComponent,
    IssueStatusChipComponent
  ],
  providers: [],
})
export class SharedModule { }
